import { IconCircleCaretDown } from '@tabler/icons-react'
import { TableColumnType } from 'antd'
import { Rule } from 'antd/lib/form'

import { SelectCaptureBlock as RenderSelectCaptureBlock } from '../../templateRenderer/CaptureBlocks/SelectCaptureBlock'
import { SelectDisplayBlock as RenderSelectDisplayBlock } from '../../templateRenderer/DisplayBlocks/SelectDisplayBlock'
import {
  DisplayBlock,
  SelectCaptureBlock,
  SelectDisplayBlock,
} from '../../types/blueprint'
import { DefaultOptionInput } from '../SideEditors/CustomInputs/SelectInput/DefaultOptionInput'

import {
  BaseBlockConfig,
  Field,
  FORBIDDEN_IN_APP,
  SelectItemsField,
  SwitchField,
  TypeOption,
} from './_BaseBlockConfig'

import { Pill } from '@/components/shared-components/BUI/Pill'
import { Flexbox } from '@/components/shared-components/Flexbox'

type CustomSelectValue = {
  disabled: boolean | undefined
  key: string | undefined
  label: string | undefined
  title: string | undefined
  value: string | undefined
}

class Config extends BaseBlockConfig<SelectCaptureBlock> {
  public override kind = 'capture' as const
  public override type = 'select' as const

  public override enableBlockToCsvColumn = true
  public override enableBlockToColumnDisplay = true

  public override blockTypeConfig: TypeOption = {
    kind: this.kind,
    value: this.type,
    label: 'Dropdown',
    icon: IconCircleCaretDown,
    helpText: 'Choose from a list of options in a dropdown',
    forbiddenTemplateTypes: FORBIDDEN_IN_APP,
  }

  public override blockFieldsConfig: Field[] = [
    {
      name: 'options',
      label: 'Options',
      field: SelectItemsField,
      required: true,
    },
    {
      name: 'defaultOption',
      label: 'Default value',
      field: { component: DefaultOptionInput },
    },
    {
      name: 'allowOther',
      label: 'Allow other options',
      helpText: 'Whether you can type in any answer',
      field: SwitchField,
    },
    {
      name: 'multiSelect',
      label: 'Multi-select',
      helpText: 'Whether multiple options can be selected',
      field: SwitchField,
    },
    {
      name: 'searchable',
      label: 'Searchable',
      helpText:
        'Whether items in the dropdown can be searched for by typing. Helpful for large lists',
      field: SwitchField,
    },
  ]

  public override mapFormValueToBlock(
    block: SelectCaptureBlock,
    value: CustomSelectValue | CustomSelectValue[],
    returnDefaultValue?: boolean
  ): SelectCaptureBlock {
    if (returnDefaultValue) {
      if (block.defaultOption) {
        const defaultOption = block.options?.find(
          (option) => option.label === block.defaultOption
        )

        if (defaultOption) {
          return {
            ...block,
            options: block.options?.map((option) => ({
              ...option,
              selected: option.value === defaultOption.value,
            })),
          }
        }
      }

      return block
    }

    const valueArray =
      (Array.isArray(value) ? value : [value]).filter((v) => {
        return Boolean(v?.value)
      }) ?? []

    // mark any pre-existing options as selected
    const options: SelectCaptureBlock['options'] = block?.options?.map(
      (option) => ({
        ...option,
        selected: Boolean(valueArray.find((v) => v.value === option.value)),
      })
    )

    // add any custom values
    valueArray?.forEach((value) => {
      if (!options?.some((option) => option.value === value.value)) {
        options.push({
          value: `${block.id}-${value.value}`,
          label: value.label ?? value.value,
          selected: true,
        })
      }
    })

    return { ...block, options }
  }

  public override mapBlockToDisplayBlock(
    block: SelectCaptureBlock
  ): DisplayBlock {
    const { options, ...restOfBlock } = block

    const selectedOptions = options?.filter((option) => option.selected)

    return {
      ...restOfBlock,
      kind: 'display',
      options: selectedOptions,
    }
  }

  public override mapBlockToColumnDisplay(
    block: SelectCaptureBlock
  ): React.ReactNode {
    const selectedOptions = block.options?.filter((o) => o.selected) ?? []

    return (
      <Flexbox gap="0.5rem" alignItems="center" flexWrap="wrap">
        {selectedOptions.map((option) => (
          <Pill key={option.label} size="small" label={option.label} whiteBg />
        ))}
      </Flexbox>
    )
  }

  public override mapBlockToCsvValue(block: SelectCaptureBlock): string | null {
    return block.options
      ?.filter((o) => o.selected)
      .map((option) => option.label)
      .join(', ')
  }

  public override mapBlockToColumnSort(
    block: SelectCaptureBlock
  ): TableColumnType<unknown> {
    return {
      filters: block?.options?.map((option) => ({
        text: option.label,
        value: option.value,
      })),
      onFilter: (value: string, record) => {
        const foundBlock = record[block.id]

        if (!foundBlock) return false

        const selected = foundBlock.options
          .filter((o) => o.selected)
          .map((o) => o.value)

        return selected.includes(value)
      },
    }
  }

  public override renderBlock(
    block: SelectCaptureBlock,
    rules?: Rule[]
  ): React.ReactNode {
    return <RenderSelectCaptureBlock block={block} rules={rules} />
  }

  public override renderDisplayBlock(
    block: SelectDisplayBlock
  ): React.ReactNode {
    return <RenderSelectDisplayBlock block={block} />
  }
}

export const SelectCaptureBlockConfig = new Config()
